import {
    ShippingProvider,
    ShippingProviderFactory,
    ShippingProviderMapperParameter,
    ShippingVehicleType,
    ShippingVehicleTypeFactory,
    ShippingVehicleTypeMapperParameter,
    ShippingVehicle,
    ShippingVehicleFactory,
    ShippingVehicleMapperParameter,
} from "../models/shipping-providers.js";
import httpMixin from "@/v2/mixins/http.js";
import mapperMixin from "@/v2/mixins/mapper.js";

export default {
    mixins: [httpMixin, mapperMixin],
    data() {
        return {
            shippingProviderUrl: window.bo.shippingProviderUrl,
        };
    },
    methods: {
        _shippingProviderMapper(shippingProvider) {
            return {
                id: shippingProvider.id,
                name: shippingProvider.name,
                type: shippingProvider.type,
                pic: shippingProvider.pic,
                pic_full_name: shippingProvider.pic_full_name,
                vehicle_types: shippingProvider.quantity,
            };
        },

        _shippingVehicleMapper(shippingVehicle) {
            return {
                id: shippingVehicle.id,
                shipping_vehicle_type_id: shippingVehicle.shipping_vehicle_type_id,
                vehicle_id: shippingVehicle.vehicle_id,
                vehicle_name: shippingVehicle.vehicle_name,
                location: shippingVehicle.location,
                default_driver: shippingVehicle.default_driver,
            };
        },

        parseShippingProvider(shippingProvider) {
            return ShippingProviderFactory.construct(
                this.createMapperParameter(new ShippingProviderMapperParameter()),
                shippingProvider
            );
        },

        parseShippingProviders(shippingProviders) {
            let parsedShippingProviders = [];

            shippingProviders.forEach((shippingProviderData) => {
                const shippingProvider = this.parseShippingProvider(shippingProviderData);
                parsedShippingProviders.push(shippingProvider);
            });

            return parsedShippingProviders;
        },

        parseShippingVehicleType(shippingVehicleType) {
            return ShippingVehicleTypeFactory.construct(
                this.createMapperParameter(new ShippingVehicleTypeMapperParameter()),
                shippingVehicleType
            );
        },

        parseShippingVehicleTypes(shippingVehicleTypes) {
            let parsedShippingVehicleTypes = [];

            shippingVehicleTypes.forEach((shippingVehicleTypeData) => {
                const shippingVehicleType = this.parseShippingVehicleType(shippingVehicleTypeData);
                parsedShippingVehicleTypes.push(shippingVehicleType);
            });

            return parsedShippingVehicles;
        },

        parseShippingVehicle(shippingVehicle) {
            return ShippingVehicleFactory.construct(
                this.createMapperParameter(new ShippingVehicleMapperParameter()),
                shippingVehicle
            );
        },

        parseShippingVehicles(shippingVehicles) {
            let parsedShippingVehicles = [];

            shippingVehicles.forEach((shippingVehicleData) => {
                const shippingVehicle = this.parseShippingVehicle(shippingVehicleData);
                parsedShippingVehicles.push(shippingVehicle);
            });

            return parsedShippingVehicles;
        },

        parseShippingProviderType(shippingProviderType) {
            let parsedShippingProviderType = {};
            if (shippingProviderType && shippingProviderType.length) {
                let words = shippingProviderType.split("_");

                let upperWords = [];

                for (let word of words) {
                    upperWords.push(word.charAt(0).toUpperCase() + word.slice(1));
                }

                parsedShippingProviderType = {
                    key: shippingProviderType,
                    label: upperWords.join(" "),
                };
            }

            return parsedShippingProviderType;
        },

        parseShippingProviderTypes(shippingProviderTypes) {
            let parsedShippingProviderTypes = [];

            if (shippingProviderTypes && shippingProviderTypes.length) {
                shippingProviderTypes.forEach((shippingProviderType) => {
                    parsedShippingProviderTypes.push(this.parseShippingProviderType(shippingProviderType));
                });
            }

            return parsedShippingProviderTypes;
        },

        getShippingProvider(id, payload, success, failure) {
            return this.getData(
                `${this.shippingProviderUrl}/${id}`,
                payload,
                (response) => {
                    success(this.parseShippingProvider(response.data));
                },
                failure
            );
        },

        getShippingProviders(payload, success, failure) {
            return this.getData(
                this.shippingProviderUrl,
                payload,
                (response) => {
                    success(this.parseShippingProviders(response.data.data), response.data.meta);
                },
                failure
            );
        },

        createShippingProvider(payload, success, failure) {
            return this.postData(
                this.shippingProviderUrl,
                payload,
                (response) => {
                    success(this.parseShippingProvider(response.data));
                },
                failure
            );
        },

        updateShippingProvider(id, payload, success, failure) {
            return this.putData(
                `${this.shippingProviderUrl}/${id}`,
                payload,
                (response) => {
                    success(this.parseShippingProvider(response.data));
                },
                failure
            );
        },

        createOrUpdateShippingProvider(id = null, payload, success, failure) {
            if (id == null) {
                return this.createShippingProvider(payload, success, failure);
            } else {
                return this.updateShippingProvider(id, payload, success, failure);
            }
        },

        deleteShippingProvider(id, payload, success, failure) {
            return this.deleteData(
                `${this.shippingProviderUrl}/${id}`,
                payload,
                (response) => {
                    success(response.data);
                },
                failure
            );
        },

        deleteShippingProviders(payload, success, failure) {
            return this.postBulkData(
                this.shippingProviderUrl,
                null,
                null,
                payload,
                (response) => {
                    success(response.data);
                },
                failure
            );
        },

        refreshShippingProvider(id, shippingProviders, success, failure) {
            this.getShippingProvider(
                id,
                this.shippingProviderUrl,
                null,
                (shippingProvider) => {
                    shippingProviders[id] = shippingProvider;
                    if (success) success(shippingProvider);
                },
                failure
            );
        },

        getPics(url, payload, success, failure) {
            return this.getData(
                url,
                payload,
                (response) => {
                    success(response.data.data, response.data.meta);
                },
                failure
            );
        },

        getLocations(url, payload, success, failure) {
            return this.getData(
                url,
                payload,
                (response) => {
                    success(response.data.data, response.data.meta);
                },
                failure
            );
        },

        getDefaultDrivers(url, payload, success, failure) {
            return this.getData(
                url,
                payload,
                (response) => {
                    success(response.data.data, response.data.meta);
                },
                failure
            );
        },
    },
};
